<template>
  <div class="input-emaillist">
    <input-text
      :value="emailInputString"
      :label="label ?? $t('actions.addEmail')"
      :autocomplete-options="autocompleteOptionsWithoutEntriesInList"
      :error="error"
      :disabled="disabled"
      :prevent-submit="true"
      :placeholder="$t('messages.inputEmailAddresses')"
      @enter="submit"
      @update:value="updateEmailInputString"
    >
      <template v-if="maxEmails !== Infinity" #top-right>
        <span class="input-emaillist__limit">
          {{ remainingEmails }} / {{ maxEmails }}
        </span>
      </template>
    </input-text>

    <slot></slot>

    <button
      class="button-icon input-emaillist__button"
      type="button"
      :disabled="disabled"
      @click="submit"
    >
      <lucide-icon name="plus" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

import InputText from "@core/input/InputText.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    /**
     * Emails that already exist in the list
     */
    emails: string[];
    /**
     * Emails that have been added in this session (to avoid duplicates)
     */
    addedEmails?: string[];
    /**
     * Options for autocompletion
     */
    autocompleteOptions: { value: string; label: string }[];
    maxEmails?: number;
    disabled?: boolean;
    label?: string | null;
  }>(),
  {
    emails: () => [],
    addedEmails: () => [],
    autocompleteOptions: () => [],
    maxEmails: Infinity,
    disabled: false,
    label: null
  }
);
const emit = defineEmits<{
  (e: "add", email: string): void;
}>();

const { t } = useI18n();
const error = ref<string | undefined>(undefined);
const emailInputString = ref("");

const autocompleteOptionsWithoutEntriesInList = computed(() => {
  return props.autocompleteOptions.filter(
    (option) => !props.emails.includes(option.value)
  );
});
const maxEmailsReached = computed(() => {
  return props.emails.length >= props.maxEmails;
});
const remainingEmails = computed(() => {
  return props.maxEmails - props.emails.length;
});
const empty = computed(() => {
  return emailInputString.value.trim() === "";
});

function cleanEmail(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-zA-Z0-9!#$%&'*+\-/=?^_`{|}~@.äÄöÖüÜß]+/gm, " ")
    .trim();
}

function updateEmailInputString(value: string) {
  // split any email list
  const emailString = cleanEmail(value);
  const emails = emailString.split(" ");

  if (emails.length <= 1) {
    error.value = undefined;
    emailInputString.value = value;
    return;
  }

  for (let i = 0; i < emails.length - 1; i++) {
    const email = emails[i];

    if (!addToEmailList(email)) {
      emailInputString.value = emails.slice(i).join(" ");
      return false;
    }

    console.log(emails.length, maxEmailsReached.value);
  }

  emailInputString.value = emails[emails.length - 1];
  error.value = undefined;
}

function addToEmailList(possibleEmail: string) {
  if (props.disabled) return false;
  const cleanedEmail = cleanEmail(possibleEmail);
  if (props.emails.includes(cleanedEmail)) {
    error.value = t("errors.emailAlreadyInList");
    return false;
  }
  if (props.addedEmails.includes(cleanedEmail)) {
    // already added in this session
    return true;
  }
  if (maxEmailsReached.value) {
    error.value = t("errors.maxEmailsReached");
    return false;
  }
  if (!isAnEmail(cleanedEmail)) {
    console.log("failed adding", cleanedEmail);
    error.value = `"${cleanedEmail}" ist keine gültige E-Mail-Adresse`;
    return false;
  }

  emit("add", cleanedEmail);
  return true;
}

function submit() {
  if (emailInputString.value == "") return true;
  if (addToEmailList(emailInputString.value)) {
    emailInputString.value = "";
    return true;
  } else {
    return false;
  }
}

function isAnEmail(value: string) {
  const emailCheck = /^\S+@\S+$/;
  return emailCheck.test(value);
}

defineExpose({
  empty,
  submit,
});
</script>

<style lang="scss">
.input-emaillist {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding-bottom: 0.25rem;

  &__button {
    margin-top: 1.125rem;
  }

  &__input {
    margin-bottom: 0.5rem;
  }

  &__limit {
    white-space: nowrap;
    padding-bottom: 0.25rem;
    font-weight: 600;
    color: var(--color-text-medium);
  }
}
</style>
